// src/hooks/admins/useAdmins.ts
import {useMemo} from "react";
import {
  useGetAdminsQuery,
  useUpdateAdminMutation,
  useDeleteAdminMutation,
  useBulkDeleteAdminsMutation,
} from "../store/api/adminsApi";
import type {UserFilterDto} from "../types/users";
import {useToast} from "./common/useToast";

export const useAdmins = (filters: UserFilterDto = {}) => {
  const {error} = useToast();

  // Query admins data
  const {
    data: adminsData,
    isLoading,
    error: queryError,
    refetch,
  } = useGetAdminsQuery(filters);

  // Extract admins, pagination, and stats from response
  const admins = useMemo(() => adminsData?.data || [], [adminsData]);
  const pagination = useMemo(() => adminsData?.pagination, [adminsData]);
  const stats = useMemo(() => adminsData?.stats, [adminsData]);

  // Mutations
  const [updateAdminMutation] = useUpdateAdminMutation();
  const [deleteAdminMutation] = useDeleteAdminMutation();

  const [bulkDeleteMutation] = useBulkDeleteAdminsMutation();

  // Handle update admin
  const handleUpdate = async (id: number, data: any) => {
    try {
      await updateAdminMutation({id, data}).unwrap();
      return true;
    } catch (err: any) {
      error("Error", err?.data?.message || "Failed to update admin");
      return false;
    }
  };

  // Handle delete admin
  const handleDelete = async (id: number) => {
    try {
      await deleteAdminMutation(id).unwrap();
      return true;
    } catch (err: any) {
      error("Error", err?.data?.message || "Failed to delete admin");
      return false;
    }
  };

  // Handle bulk delete admins
  const handleBulkDelete = async (ids: number[]) => {
    try {
      await bulkDeleteMutation(ids).unwrap();
      return true;
    } catch (err: any) {
      error("Error", err?.data?.message || "Failed to delete admins");
      return false;
    }
  };

  // Show error toast if query fails
  if (queryError) {
    error("Error", "Failed to load admins data");
  }

  return {
    admins,
    pagination,
    stats,
    isLoading,
    queryError,
    refetch,
    handleUpdate,
    handleDelete,

    handleBulkDelete,
  };
};
